import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { siteRequest, safeReturn } from "lib/siteCustomerApi";
import SignIn from "./SignIn";
import { notifyWebsiteSessionChanged } from "lib/siteAccessEvents";
import Library from "./Library";
import PartnerPortal, { PartnerNavigation } from "./PartnerPortal";
import Courses, { CourseEnrollment } from "./Courses";
import { Profile, Sessions, Activity, Support, SupportThread } from "./Account";
import { Notice } from "./Shared";
import Navigation from "components/Navigation";
import Footer from "components/Footer";
import "./AccessWorkspace.css";
import Home from "./Home";
import Orders from "./Orders";
import { FileText, GraduationCap, House, LogOut, Package, UserRound } from "lucide-react";
import { RouteTabs, useAppEffects } from "components/ios/Effects";
import { useConfirm } from "components/ios/Alert";
import { useWallpaper } from "components/ios/Wallpaper";
import { AppearanceControls, useClaimThemeControl } from "components/ios/Appearance";
const TMS_PANEL = process.env.REACT_APP_CUSTOMER_TMS_PANEL === 'true';
const sections = [
  ["", "Overview"],
  ["orders", "Orders"],
  ["courses", "Learning"],
  ["files", "Free resources"],
  ["profile", "Account"],
  // The old CarrierFlow panel is switched off until the TMS link is on (L8); its code stays until Ivan approves removal.
  ...(TMS_PANEL ? [["tms", "CarrierFlow"]] : []),
];
const accountPages = [
  ["profile", "Profile"],
  ["sessions", "Signed-in devices"],
  ["activity", "Activity"],
  ["support", "Support"],
];
const tabOf = (rest) => {
  const first = rest.split("/")[0];
  if (["profile", "sessions", "activity", "support", "partners"].includes(first)) return "profile";
  if (first === "enroll") return "courses";
  return sections.some(([p]) => p === first) ? first : "";
};
const readProof = () => {
  const q = new URLSearchParams(window.location.hash.slice(1));
  return q.has("challenge") && q.has("token")
    ? { challengeId: q.get("challenge"), token: q.get("token") }
    : null;
};
export default function AccessWorkspace({ paid }) {
  const [proof, setProof] = useState(readProof),
    [state, setState] = useState({ loading: true }),
    [logoutError, setLogoutError] = useState(null),
    [signingOut, setSigningOut] = useState(false),
    [epoch, setEpoch] = useState(0);
  const { effects, setEffects } = useAppEffects();
  useClaimThemeControl();
  const wallpaper = useWallpaper();
  const [confirmDialog, ask] = useConfirm();
  const location = useLocation(),
    nav = useNavigate(),
    sequence = useRef(0),
    channel = useRef(null),
    heading = useRef(null);
  const refresh = useCallback(async () => {
    const n = ++sequence.current;
    try {
      const result = await siteRequest("/customer/profile");
      if (n === sequence.current) setState({ profile: result.data });
    } catch (error) {
      if (n === sequence.current)
        setState(error.status === 401 ? {} : { error });
    }
  }, []);
  useLayoutEffect(() => {
    if (proof)
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
  }, [proof]);
  useEffect(() => {
    const changed = () => {
      const value = readProof();
      if (value) setProof(value);
    };
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  useEffect(() => {
    refresh();
    let closed = false;
    const invalidateRequests = () => { sequence.current++; };
    const verify = () => {
      if (!closed && document.visibilityState === "visible") refresh();
    };
    const ended = () => {
      invalidateRequests();
      setState({});
      setEpoch((v) => v + 1);
    };
    const changed = () => {
      invalidateRequests();
      setState({ loading: true });
      setEpoch((v) => v + 1);
      refresh();
    };
    if (typeof BroadcastChannel !== "undefined") {
      channel.current = new BroadcastChannel("magellan-website-access");
      channel.current.onmessage = changed;
    }
    const timer = setInterval(verify, 30000);
    window.addEventListener("focus", verify);
    window.addEventListener("pageshow", verify);
    window.addEventListener("site-access-ended", ended);
    window.addEventListener("site-access-changed", changed);
    return () => {
      closed = true;
      invalidateRequests();
      clearInterval(timer);
      channel.current?.close();
      window.removeEventListener("focus", verify);
      window.removeEventListener("pageshow", verify);
      window.removeEventListener("site-access-ended", ended);
      window.removeEventListener("site-access-changed", changed);
    };
  }, [refresh]);
  const lastPath = useRef("");
  useEffect(() => {
    // Moving between orders keeps focus with the split view (it moves focus to the card and back).
    const orders = (path) => /^\/customer\/access\/orders(\/|$)/.test(path);
    if (!(orders(location.pathname) && orders(lastPath.current))) heading.current?.focus();
    lastPath.current = location.pathname;
  }, [location.pathname]);
  useEffect(() => {
    const previous = document.title,
      old = document.head.querySelector('meta[name="referrer"]'),
      meta = old || document.createElement("meta"),
      value = old?.content,
      oldRobots = document.head.querySelector('meta[name="robots"]'),
      robots = oldRobots || document.createElement("meta"),
      robotsValue = oldRobots?.content;
    robots.name = "robots";
    robots.content = "noindex,nofollow";
    if (!oldRobots) document.head.appendChild(robots);
    meta.name = "referrer";
    meta.content = "no-referrer";
    if (!old) document.head.appendChild(meta);
    document.title = "Customer Access | Magellan";
    return () => {
      document.title = previous;
      if (old) old.content = value;
      else meta.remove();
      if (oldRobots) oldRobots.content = robotsValue;
      else robots.remove();
    };
  }, []);
  const signedIn = async () => {
    setEpoch((v) => v + 1);
    await refresh();
    notifyWebsiteSessionChanged();
    if (safeReturn(new URLSearchParams(window.location.search).get("returnPath")) === "/resources") window.location.assign("/resources");
    else nav(safeReturn(location.pathname), { replace: true });
  };
  const isTms = TMS_PANEL && location.pathname === "/customer/access/tms";
  const rest = location.pathname.replace(/^\/customer\/access\/?/, "");
  const tab = tabOf(rest);
  const signOut = async () => {
    if (signingOut) return;
    if (!(await ask({ title: "Sign out?", message: "You will need to sign in again to see your orders and courses.", confirm: "Sign out", destructive: true }))) return;
    setSigningOut(true);
    setLogoutError(null);
    try {
      await siteRequest("/customer/logout", { method: "POST", body: {} });
      sequence.current++;
      setState({});
      setEpoch((v) => v + 1);
      notifyWebsiteSessionChanged();
      nav("/customer/access", { replace: true });
    } catch (error) {
      if (error.status === 401) {
        setState({});
        notifyWebsiteSessionChanged();
      } else setLogoutError(error);
    } finally {
      setSigningOut(false);
    }
  };
  const name = state.profile?.name || state.profile?.email || "";
  const initials = name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  const showNav = Boolean(state.profile || isTms);
  return (
    <>
      <Navigation />
      <div className={`customer-access ios-app ${showNav ? "ca-signed-in" : ""}`} style={wallpaper.style}>
        <a className="ca-skip" href="#access-main">
          Skip to account
        </a>
        <header className="ca-header">
          <span className="ca-eyebrow">My Magellan</span>
          <div className="ca-actions">
            <Link to="/" className="ios-btn ios-btn-gray">Visit website ↗</Link>
            {state.profile && (
              <>
                <span className="ca-avatar" aria-hidden="true">{initials || "M"}</span>
                <button className="ios-btn ios-btn-gray" disabled={signingOut} onClick={signOut}>
                  <LogOut size={15} aria-hidden="true" /> {signingOut ? "Signing out…" : "Sign out"}
                </button>
              </>
            )}
          </div>
        </header>
        {showNav && (
          <div id="access-navigation" className="ca-nav-wrap">
            <RouteTabs
              label="Customer account"
              className="ca-tabs"
              items={sections.map(([path, title]) => ({ to: "/customer/access" + (path ? "/" + path : ""), label: title, active: tab === path }))}
            />
          </div>
        )}
        {showNav && tab === "profile" && (
          <div className="ca-subnav">
            <RouteTabs
              label="Account pages"
              items={[...accountPages, ...(TMS_PANEL ? [["tms", "CarrierFlow"]] : [])].map(([path, title]) => ({
                to: "/customer/access/" + path,
                label: title,
                active: rest === path || rest.startsWith(path + "/"),
              }))}
            />
            {state.profile && <PartnerNavigation key={`${state.profile.id}:${epoch}`} profile={state.profile} />}
          </div>
        )}
        <main id="access-main" ref={heading} tabIndex={-1} className="ca-main">
          <Notice error={logoutError} />
          {isTms ? (
            <>
              <p className="ca-eyebrow">Your CarrierFlow account</p>
              <h1>CarrierFlow</h1>
              <p className="ca-lead">
                Manage purchases, invoices, subscriptions, agreements, academy
                lessons and webinars through your existing account.
              </p>
              <p className="ca-notice">
                CarrierFlow sign-in and current purchase permissions apply here.
                If its connection is unavailable, your website account
                remains available.
              </p>
              <Link to="/customer/access">‹ My Magellan</Link>
              <div className="ca-paid ios-card">{paid}</div>
            </>
          ) : proof ? (
            <SignIn
              proof={proof}
              clearProof={() => setProof(null)}
              onSignedIn={signedIn}
              profile={state.profile}
            />
          ) : state.loading ? (
            <p role="status">Checking your website session…</p>
          ) : state.profile ? (
            <div key={`${state.profile.id}:${epoch}`}>
              <Routes>
                <Route index element={<><h1>My Magellan</h1><Home profile={state.profile} /></>} />
                <Route path="orders/:orderId?" element={<><h1>Orders</h1><Orders /></>} />
                <Route path="files" element={<Library />} />
                <Route path="partners" element={<PartnerPortal profile={state.profile} />} />
                <Route path="partners/:partnerId" element={<PartnerPortal profile={state.profile} />} />
                <Route path="courses" element={<Courses />} />
                <Route path="courses/:enrollmentId" element={<Courses />} />
                <Route path="enroll/:courseId" element={<CourseEnrollment profile={state.profile} />} />
                <Route
                  path="profile"
                  element={
                    <>
                      <Profile profile={state.profile} onRefresh={refresh} />
                      <section className="ca-panel ca-appearance" aria-labelledby="ca-appearance-title">
                        <h2 id="ca-appearance-title">Appearance</h2>
                        <AppearanceControls effects={effects} setEffects={setEffects} />
                      </section>
                    </>
                  }
                />
                <Route
                  path="sessions"
                  element={<Sessions onRefresh={refresh} />}
                />
                <Route path="activity" element={<Activity />} />
                <Route path="support" element={<Support />} />
                <Route path="support/:ticketId" element={<SupportThread />} />
                <Route
                  path="*"
                  element={
                    <>
                      <h1>Page unavailable</h1>
                      <Link to="/customer/access">Open My Magellan</Link>
                    </>
                  }
                />
              </Routes>
            </div>
          ) : (
            <>
              <Notice error={state.error} />
              {state.error && (
                <button onClick={refresh}>Retry website connection</button>
              )}
              <SignIn onSignedIn={signedIn} />
            </>
          )}
        </main>
        {confirmDialog}
        {showNav && (
          <nav className="ios-tabbar" aria-label="Account quick navigation" data-glass="bar">
            {[["", "Home", House], ["orders", "Orders", Package], ["courses", "Learning", GraduationCap], ["files", "Files", FileText], ["profile", "Account", UserRound]].map(([path, label, Icon]) => (
              <NavLink key={path} end={!path} to={"/customer/access" + (path ? "/" + path : "")} className={tab === path ? "active" : undefined}>
                <Icon size={22} aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
      <Footer />
    </>
  );
}
